import CompareColumnLayout from "../ui/CompareColumnLayout";

import TraceDetailHeader from "./TraceDetailHeader";
import TraceSpanDetailsPanel from "./TraceSpanDetailsPanel";
import TraceSpanHierarchy from "./TraceSpanHierarchy";
import type { TraceDetailState } from "./hooks/useTraceDetailState";
import { findSpanById } from "./utils";

import LoadingSpinner from "@/components/LoadingSpinner";

const TraceDetailContent = ({
  traceId,
  error,
  loggedBeforeRetentionPolicy,
  flattenedSpans,
  isHighlighted,
  isLoading,
  isWorkflow,
  selectedSpan,
  setSelectedSpan,
  traceInput,
  traceFinalOutput,
  traceEntities,
  setTimeRange,
  spanStatuses,
  spanTree,
  table,
  timeRange,
  userToken,
  workflow,
}: TraceDetailState) => {
  // Trace-level Input / Final Output only decorate the root span's panel
  const rootSpanSelected = Boolean(
    selectedSpan &&
      (!selectedSpan.parent_id ||
        !flattenedSpans.some(span => span.span_id === selectedSpan.parent_id)),
  );
  const jumpToSpan = (spanId: string) => {
    const target = findSpanById(flattenedSpans, spanId);
    if (target) {
      setSelectedSpan(target);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size={12} />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500">Error: {error.message}</div>;
  }

  return (
    <div className="flex h-full flex-col overflow-hidden px-6 pt-8">
      <div className="flex-shrink-0">
        <TraceDetailHeader
          traceId={traceId}
          traceName={spanTree?.[0]?.name ?? null}
          selectedSpan={selectedSpan}
          spanTree={spanTree}
          loggedBeforeRetentionPolicy={loggedBeforeRetentionPolicy}
          flattenedSpans={flattenedSpans}
          setSelectedSpan={setSelectedSpan}
        />
      </div>
      <div className="flex-grow overflow-hidden pb-4 pt-4">
        {!spanTree?.length ? (
          <p className="p-4 text-gray-500">No spans found for this trace.</p>
        ) : (
          <div className="h-full">
            <CompareColumnLayout
              leftComponent={
                <TraceSpanHierarchy
                  flattenedSpans={flattenedSpans}
                  isHighlighted={isHighlighted}
                  setSelectedSpan={setSelectedSpan}
                  setTimeRange={setTimeRange}
                  spanStatuses={spanStatuses}
                  spanTree={spanTree}
                  table={table}
                  timeRange={timeRange}
                />
              }
              rightComponent={
                <TraceSpanDetailsPanel
                  isLoading={isLoading}
                  isWorkflow={isWorkflow}
                  selectedSpan={selectedSpan}
                  traceInput={rootSpanSelected ? traceInput : null}
                  traceFinalOutput={rootSpanSelected ? traceFinalOutput : null}
                  traceEntities={rootSpanSelected ? traceEntities : []}
                  traceRunning={flattenedSpans.some(span => !span.end)}
                  onJumpToSpan={jumpToSpan}
                  userToken={userToken}
                  workflow={workflow}
                />
              }
              minimal
              initialLeftWidth={40}
              minWidth={25}
              rightColumnClassName="rounded-lg border bg-white shadow-sm"
              leftColumnClassName="rounded-lg border bg-white shadow-sm"
              gap="1rem"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TraceDetailContent;
